import React, { Component } from 'react';
import PropTypes from 'prop-types';
import accounting from 'accounting';
import moment from 'moment';
import { titleize } from 'underscore.string';
import SessionStore from 'stores/SessionStore';
import { Translation, withTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { BOOKING_ACCESSORIALS } from '../../../config/accessorials';
import { calculatePalletSpaces } from '../../../helpers/shipments';
import { sluggify } from '../../../utils/localization/utils';
import {
  LoadDimensionFormatter,
  LoadUnitFormatter,
} from '../master/formatters';

const Location = ({
  label,
  location: { name, street, suite_or_apt, city, state, zipcode },
}) => {
  const address = street || name;
  return (
    <Translation>
      {(t, { i18n }) => (
        <div>
          <h4 className="text-lg text-grey-dark">{label}</h4>
          <div className="mb-4 flex">
            <i className="fa fa-map-marker mr-2" />
            <div>
              {name && (
                <p className="mb-1 text-grey-dark font-bold my-0">{name}</p>
              )}
              <p className="mb-1">
                {address}{' '}
                {suite_or_apt && (
                  <span>{t('SUITE_WITH_VALUE', { suite: suite_or_apt })}</span>
                )}
              </p>
              <p>
                {city}, {state} {zipcode}
              </p>
            </div>
          </div>
        </div>
      )}
    </Translation>
  );
};

Location.propTypes = {
  label: PropTypes.string,
  location: PropTypes.shape({
    street: PropTypes.string,
    suite_or_apt: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipcode: PropTypes.string,
  }),
};

class QuoteDetail extends Component {
  static propTypes = {
    quote: PropTypes.shape({
      quote_id: PropTypes.string.isRequired,
      total_cost: PropTypes.number.isRequired,
    }),
  };
  renderOrigin() {
    const {
      quote: { origin },
    } = this.props;
    return (
      <Translation>
        {(t, { i18n }) => (
          <Location label={titleize(t('ORIGIN'))} location={origin[0]} />
        )}
      </Translation>
    );
  }
  renderDestination() {
    const {
      quote: { destination },
    } = this.props;
    return (
      <Translation>
        {(t, { i18n }) => (
          <Location label={t('DESTINATION')} location={destination[0]} />
        )}
      </Translation>
    );
  }
  renderLoads() {
    const {
      quote: { loads },
    } = this.props;

    if (!loads.length) {
      return null;
    }

    const totalPalletSpaces = calculatePalletSpaces(loads);

    return (
      <Translation>
        {(t, { i18n }) => (
          <div className="mb-4">
            {loads.map((load) => (
              <div>
                <p className="text-lg">
                  <LoadUnitFormatter
                    isTotalWeight
                    weight={accounting.format(load.weight)}
                    weight_units={load.weight_units}
                  />
                </p>
                <p className="text-lg">
                  <span>
                    {load.quantity} {titleize(t(sluggify(load.type)))}
                  </span>
                  <span>, </span>
                  <LoadDimensionFormatter
                    length={load.length}
                    width={load.width}
                    height={load.height}
                    dimensional_units={load.dimensional_units}
                  />
                  {load.spaces && (
                    <span>
                      ,{' '}
                      {t('PALLET_SPACE_WITH_VALUE', {
                        palletSpace: load.spaces,
                      })}
                    </span>
                  )}
                </p>
              </div>
            ))}
            {SessionStore.isAdmin() && (
              <p className="text-lg">
                {t('TOTAL_PALLET_SPACE_WITH_VALUE', {
                  totalPalletSpace: totalPalletSpaces,
                })}
              </p>
            )}
          </div>
        )}
      </Translation>
    );
  }

  renderAccessorials() {
    const {
      quote: { accessorials: currentAcessorials },
    } = this.props;

    const keys = Object.keys(currentAcessorials || {});
    if (!keys.length) {
      return null;
    }

    const selectedAccessorials = BOOKING_ACCESSORIALS.filter((a) =>
      keys.includes(a.value),
    );

    return (
      <Translation>
        {(t, { i18n }) => (
          <div>
            <h3 className="text-xl mb-2">{t('ACCESSORIAL', { count: 2 })}</h3>
            <div>
              {selectedAccessorials.map((accessorial) => (
                <div className="mb-2 flex items-center">
                  <img
                    src={accessorial.picture_url}
                    alt={t(accessorial.localizeLabel)}
                    title={t(accessorial.localizeDescription)}
                    className="rounded-full block w-12 h-12 object-cover bg-green border-grey-lighter border-2 border-solid mr-2"
                  />
                  <span>
                    {t(
                      accessorial.localizeLabel ||
                        accessorial.localizeDescription,
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Translation>
    );
  }
  render() {
    const {
      quote: { _id, quote_id, total_cost, shipment_type, expiration_date },
    } = this.props;

    return (
      <Translation>
        {(t, { i18n }) => (
          <div className="bg-white p-4 h-screen" key="quote-details">
            <div>
              <h3 className="text-xl mb-1">{quote_id}</h3>
              <small className="italic mb-2 text-grey-darker">
                {t(sluggify(shipment_type))}
              </small>
              <h4 className="text-2xl mb-4">
                <small>
                  {t('ESTIMATE_WITH_VALUE', {
                    estimate: formatMoney(total_cost),
                  })}
                  :
                </small>
              </h4>
              <small className="text-grey-darker flex items-center">
                {t('EXPIRES_ON_WITH_VALUE', {
                  date: moment(expiration_date).format('L'),
                })}
                &nbsp;
              </small>

              {this.renderOrigin()}
              {this.renderDestination()}
              {this.renderLoads()}
              {this.renderAccessorials()}
            </div>
            <div className="actions my-8">
              <a
                href={`/booking/${_id}`}
                className="button button-green block text-center"
              >
                {t('BOOK_A_SHIPMENT')}
              </a>
            </div>
          </div>
        )}
      </Translation>
    );
  }
}

export default withTranslation()(QuoteDetail);
